import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { ComboBox, DropDownList } from '@progress/kendo-react-dropdowns';
import { Input } from '@progress/kendo-react-inputs';
import React, { useEffect, useState } from 'react';
import { DocumentType } from 'TypeGen/Documents/document-type';
import { IDNameViewModel } from 'TypeGen/id-name-view-model';
import { JsonResponse } from 'TypeGen/json-response';
import LoadingPanel from '../../../components/LoadingPanel';
import { fetchApi } from '../../../services/api';
import { SelectedCarrierValue } from '../CarrierGrid';

interface Props {
  SelectedCarrier: SelectedCarrierValue | null;
  CloseDialog: () => void;
}

const DocumentTypes: IDNameViewModel[] = [
  { ID: DocumentType.CarrierBrokerageAgreement, Name: "Brokerage Carrier Agreement" },
//{ ID: DocumentType.CarrierProfile, Name: "Carrier Profile" },
  { ID: DocumentType.CtpatQuestionnaire, Name: "CTPAT Questionnaire" },
//  { ID: 0, Name: "Highway Onboarding" },
  { ID: 1, Name: "Highway Invitation" },
];

const SendBrokerageCarrierAgreementDialog: React.FC<Props> = ({ SelectedCarrier, CloseDialog }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [documentTypeID, setDocumentTypeID] = useState<number>(-1);
  const [email, setEmail] = useState<string>('');
  const [mcNumber, setMCNumber] = useState<string>('');

  useEffect(() => {
    if (SelectedCarrier === null) return;
    setIsLoading(true);
    fetchApi(`/api/Asset/BrokerageCarrierAgreement/${SelectedCarrier.CarrierID}`)
      .then((response) => {
        setEmails(response.Emails);
        setMCNumber(response.MCNumber || '');
        setIsLoading(false);
      });
  }, [SelectedCarrier]);

  const send = () => {
    if (documentTypeID === -1) {
      alert("Please select a document type!");
      return;
    }

    if (!email) {
      alert("Please select/enter a valid email!");
      return;
    }

    if (!mcNumber && documentTypeID !== 0) {
      alert("Please enter a Motor Carrier number!");
      return;
    }

    if (isLoading) return;

    setIsLoading(true);

    const data = {
      CarrierID: SelectedCarrier?.CarrierID,
      MCNumber: mcNumber,
      EMailAddress: email.trim(),
    };

    let url = '';
    switch (documentTypeID) {
      case DocumentType.CarrierBrokerageAgreement:
        url = `/api/Asset/BrokerageCarrierAgreement`;
        break;
      case DocumentType.CarrierProfile:
        url = `/api/Asset/CarrierProfile`;
        break;
      case DocumentType.CtpatQuestionnaire:
        url = `/api/Asset/CTPATQuestionnaire`;
        break;
      case 0:
        url = `/api/Asset/HighwayOnboarding`;
        break;
      case 1:
        url = `/api/Asset/HighwayInvitation`;
        break;
    }

    fetchApi(url, data, 'POST')
      .then((response: JsonResponse) => {
        if (response.Success) {
          setIsLoading(false);
          CloseDialog();
        } else {
          setIsLoading(false);
          alert(response.ErrorMessage);
        }
      })
      .catch((e) => {
        setIsLoading(false);
        if (!e?.status) alert("Unable to Send Carrier Document!");
      });
  };

  return (
    <Dialog title="Send Carrier Document" onClose={CloseDialog} width={500}>
      {isLoading && <LoadingPanel />}
      <div className="form-group row">
        <label className="col-md-4 col-form-label">Document Type</label>
        <div className="col-md-8">
          <DropDownList
            required
            data={DocumentTypes}
            textField="Name"
            dataItemKey="ID"
            value={DocumentTypes.find((x) => x.ID === documentTypeID)}
            onChange={(e) => setDocumentTypeID(e.target.value.ID)}
            style={{ width: "100%" }}
          />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-md-4 col-form-label">Signer Email</label>
        <div className="col-md-8">
          <ComboBox ignoreCase={true}
            onChange={(e) => setEmail(e.value)}
            allowCustom={true}
            placeholder="someone@company.com"
            value={email}
            data={emails}
            style={{ width: "100%" }}
          />
        </div>
      </div>
      {documentTypeID !== 0 && <div className="form-group row">
        <label className="col-md-4 col-form-label">Carrier MC #</label>
        <div className="col-md-8">
          <Input
            required
            value={mcNumber}
            onChange={(e) => setMCNumber(e.value)}
            placeholder="MC #"
            style={{ width: "100%" }}
          />
        </div>
      </div>}
      <DialogActionsBar layout="end">
        <Button themeColor="primary" onClick={send}>Request Signature</Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default SendBrokerageCarrierAgreementDialog;